import { useMemo, useState } from "react";

import Typography from "@/design-system/Typography";
import { FetchGet } from "@/lib/fetch-helper";
import CashbackClaim, { type ICashbackClaim } from "@/src/cashback/CashbackClaim";
import { AvailabilityState } from "@/src/cashback/CashbackCommonInterfaces";
import type { IScrollPagination } from "@/src/cashback/MainPage/ScrollPagination";
import TransactionList from "@/src/cashback/MainPage/TransactionList";
import type { ITransactionItem } from "@/src/cashback/MainPage/Transactions/shared";
import { TransactionsContext } from "@/src/cashback/MainPage/TransactionsContext";
import type { IUserCommunicationTemplate } from "@/src/common/UserCommunicationTemplate";
import UserCommunicationTemplateModal from "@/src/common/UserCommunicationTemplateModal";
import type { IUserCommunicationTemplateModal } from "@/src/common/UserCommunicationTemplateModal";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { useMutationCustom } from "@/src/query-client-overrides";
import { urlFormat } from "@Components/Modules/Helpers";
import { Cashback } from "@Routes/CashbackLinks";
import * as stylex from "@stylexjs/stylex";

import * as css from "./TemporaryTrxListPage.module.css";

export function TemporaryTrxListPage(props: { cashbackClaim: ICashbackClaim }) {
    const [showClaimModal, setShowClaimModal] = useState<boolean>(false);
    const [warningAboutComplain, setWarningAboutComplain] = useState<IUserCommunicationTemplateModal>();
    const [currentTransaction, setCurrentTransaction] = useState<ITransactionItem | null>(null);
    const { isMobileOrTablet } = useViewportInfoContext();
    const title = languageSelector(
        "Twoje transakcje w Zwrotach za zakupy",

        "Your transactions in the Cashback service",
    );

    const { mutate: validateComplaintMutation } = useMutationCustom({
        mutationFn: (trx: ITransactionItem) => {
            const response = FetchGet(
                urlFormat(
                    Cashback,
                    {
                        action: "ValidateComplaint",
                        transactionId: trx?.id,
                        complaintType: trx?.availability === AvailabilityState.Rejected ? 1 : 2,
                    },
                    true,
                ),
            );
            return response;
        },
        onSuccess: (response: IUserCommunicationTemplate) => {
            if (response) {
                const modalCustom = {
                    commBase: response,
                    modal: {
                        closeCustomTelemetry: { eventName: "common-exit", actionType: "Click" },
                        onClose: () => {
                            setWarningAboutComplain(null);
                            setCurrentTransaction(null);
                        },
                    },
                } satisfies IUserCommunicationTemplateModal;
                setWarningAboutComplain(modalCustom);

                return;
            }
            setShowClaimModal(true);
        },
        onError: () => {
            setCurrentTransaction(null);
        },
    });

    const closeClaim = () => {
        setShowClaimModal(false);
        setCurrentTransaction(null);
    };

    const value = useMemo(
        () => ({
            claim: (selectedTrx: ITransactionItem) => {
                if (selectedTrx?.id) {
                    validateComplaintMutation(selectedTrx);
                    setCurrentTransaction(selectedTrx);
                }
            },
        }),
        [validateComplaintMutation],
    );

    const claimProps = (): JSX.Element | null => {
        if (!currentTransaction) {
            return (
                <CashbackClaim
                    {...props.cashbackClaim}
                    navigationModal={{
                        ...props.cashbackClaim.navigationModal,
                        onClose: closeClaim,
                    }}
                />
            );
        }

        return (
            <CashbackClaim
                {...props.cashbackClaim}
                fields={{
                    transactionId: currentTransaction.id,
                    date: currentTransaction.date,
                    brandName: currentTransaction.brandName,
                    brandId: currentTransaction.brandId,
                    purchaseAmount: currentTransaction.purchaseAmount,
                    cashbackAmount: currentTransaction.returnedAmount,
                    orderId: currentTransaction.orderId,
                    availability: currentTransaction.availability,
                    imageUrl: currentTransaction.logo,
                }}
                navigationModal={{
                    ...props.cashbackClaim.navigationModal,
                    onClose: closeClaim,
                }}
            />
        );
    };

    return (
        <div className={css.temporaryTrxListPageComponent}>
            <div className={css.title}>
                <Typography fontStyle={isMobileOrTablet ? "hXS" : "hL"} styleX={styles.title}>
                    {title}
                </Typography>
            </div>
            <TransactionsContext.Provider value={value}>
                <TransactionList
                    transPagination={
                        {
                            pageSize: 50,
                            noScrollLoading: true,
                        } as IScrollPagination
                    }
                    shouldShowModal={() => setShowClaimModal(true)}
                    isTemporaryPage
                />

                {showClaimModal && claimProps()}
                {warningAboutComplain && <UserCommunicationTemplateModal {...warningAboutComplain} />}
            </TransactionsContext.Provider>
        </div>
    );
}

const styles = stylex.create({
    title: {
        fontWeight: "bold",
    },
});
